import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Manages popover open/close state with Escape key and click-outside dismissal.
 *
 * Usage:
 *   const { isOpen, toggle, close, containerRef } = usePopover();
 *   return (
 *     <div ref={containerRef}>
 *       <button onClick={toggle}>Open</button>
 *       {isOpen && <div>Content</div>}
 *     </div>
 *   );
 */
export function usePopover() {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        close();
      }
    };

    document.addEventListener("keydown", handleEsc);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, close]);

  return { isOpen, toggle, close, containerRef };
}
